import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {WizardModalComponent} from '../../../../../shared/components/index';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Ticket} from '../../../../../shared/models/ticket.model';
import {OperationType} from '../../../../model/operationType.model';
import {OperationTypeService} from '../../../../../core/services/operation-type.service';
import {Log} from "ng2-logger";
import {Issue} from "../../../../../shared/models/issue.model";
import {AppUtils} from '@app/shared/util/app.utils';
import {TicketType} from "@models/enums/ticket-type.enum";
import {TableConfig} from '@app/shared/util/table-config.utils';
import {ToolUsageService} from '@app/core/services/tool-usage.service';
import {MaintenancePlansService} from '@app/maintenance/service/maintenance-plans.service';
import { ToastsManager } from "@app/shared/util/toastr.service";
import {TranslateService} from '@ngx-translate/core';
import {ToolService} from '@app/core/services/tool.service';
import {MaintenancePlanUtils} from '@app/shared/util/maintenance-plan.utils';
import { RevisionInfo } from "@components/revision-info/models/revision-info.model";
import { RevisionInfoModalComponent } from "@components/revision-info-modal/revision-info-modal.component";

@Component({
    selector: 'app-ticket-promote-modal',
    templateUrl: './ticket-promote-modal.component.html',
    styleUrls: ['./ticket-promote-modal.component.scss']
})
export class TicketPromoteModalComponent implements OnInit, AfterViewInit {
    _logger = Log.create(TicketPromoteModalComponent.name);

    @ViewChild(WizardModalComponent, {static: true}) modal: WizardModalComponent;
    @ViewChild(RevisionInfoModalComponent, {static: true}) revisionInfoModal: RevisionInfoModalComponent;

    @Input() hasCancel = false;
    @Input() isLoading: boolean;

    @Output('create') create: EventEmitter<any> = new EventEmitter();

    public ticket: Ticket = new Ticket();

    ticketForm: UntypedFormGroup;
    operationsWithRecurrencesForm: UntypedFormGroup;

    issue: Issue;
    error = false;
    showValidations = false;
    operationTypes: OperationType[] = [];
    multipleTools = false;
    availableOperations: OperationType[] = [];
    revisionInfo: RevisionInfo[] = [];

    operationsWithRecurrences = [];
    maintenancePlanItems = [];
    selectedOperations = [];
    selectedRecurrencesItems: any = [];
    mandatoryMaintenancePlanItems = [];

    ticketOperations = [];
    overviewItems = [];

    constructor(private operationTypeService: OperationTypeService,
                private maintenancePlanService: MaintenancePlansService,
                private toolService: ToolService,
                private translate: TranslateService,
                private toastr: ToastsManager,
                private toolUsageService: ToolUsageService) {
    }

    ngOnInit() {
        this.onFormInit();
        this.getOperationTypes();
    }

    ngAfterViewInit() {
    }

    open(issue = new Issue(), operationTypes: OperationType[] = []) {
        this.multipleTools = issue && issue.tools[0] && issue.tools.length > 1;
        this.issue = issue;

        this.maintenancePlanItems = [];
        this.operationsWithRecurrences = [];
        this.selectedOperations = [];
        this.ticketOperations = [];

        this.loadFormValue(issue, operationTypes);

        this.loadAndDisplay(issue);
    }

    close() {
        this.modal.close();
    }

    isOpen() {
        return this.modal?.isOpen;
    }

    private loadFormValue(issue, operationTypes: OperationType[]) {
        this.ticketForm.setValue({
            description: issue.reason.name + ' - ' + issue.description,
            operationTypes: operationTypes
        });
    }

    onFormInit() {
        const ticketDescription = '';
        const ticketOperationTypes = [];

        this.ticketForm = new UntypedFormGroup({
            'description': new UntypedFormControl(ticketDescription, [Validators.required]),
            'operationTypes': new UntypedFormControl(ticketOperationTypes, [AppUtils.notEmptyValidation])
        });

        this.operationsWithRecurrencesForm = new UntypedFormGroup({
            'selectedRecurrencesItems': new UntypedFormControl([], [AppUtils.atLeastOneToolValidator(1)])
        });


        this._logger.info('onFormInit:: form initialised', this.ticketForm);
    }

    createTicket() {
        if (this.ticketForm.invalid) {
            this._logger.info('createTicket:: Form is invalid', this.ticketForm);
            this.showValidations = true;
            return;
        }

        this._logger.info('createTicket:: Form is valid', this.ticketForm);

        const ticket = this.ticketForm.value;
        ticket.issueId = this.issue.id;
        ticket.selectedTools = this.issue.tools;
        ticket.type = TicketType.Issue;
        ticket.operationTypes = MaintenancePlanUtils.computeOperationTypes(this.ticketForm.controls.operationTypes.value,
                                                                           this.mandatoryMaintenancePlanItems,
                                                                           this.operationTypes);
        ticket.ticketOperations = this.ticketOperations;

        this.create.emit(ticket);
    }

    private getOperationTypes() {
        // Observable for retrieving operation types data
        this.operationTypeService.getAllOperationTypes().subscribe(
            (data: any) => {
                this.operationTypes = data.operationTypes;
            },
            (error: any) => {
            }
        );
    }

    loadAndDisplay(issue) {
        this.isLoading = true;

        const toolsIds = issue.tools.map(x => x.id);
        const tableConfig = new TableConfig();
        toolsIds.forEach(element => {
            tableConfig.filters.push({field: 'toolIds', value: element})
        });

        this.maintenancePlanService.getMandatoryPlanItemsByToolIds(toolsIds).subscribe((data: any) => {
            this.mandatoryMaintenancePlanItems = data;
            if (this.mandatoryMaintenancePlanItems && this.mandatoryMaintenancePlanItems.length) {
                this.ticketForm.get('operationTypes').clearValidators();
                this.ticketForm.get('operationTypes').updateValueAndValidity();
            }

            this.isLoading = false;

            if (!this.modal.isOpen) {
                this.modal.open();
            }
        })
        this.availableOperations = this.operationTypes;
    }

    onStepChange(step) {
        this._logger.info('onStepChange:: ', step);

        if (!this.toolsHaveMaintenancePlan()) {
            return;
        }

        if (step === 1) {
            this.selectedOperations = this.ticketForm.controls.operationTypes.value.map(x => x.id);
            const toolIds = this.issue.tools.map(t => t.id);
            this.isLoading = true;


            this.maintenancePlanService.getPlanItemsByToolIds(toolIds).subscribe((data: any) => {
                this.maintenancePlanItems = data;

                // Obtain all the  operation (selected and mandatory)
                const allOperationIds = this.mandatoryMaintenancePlanItems && this.mandatoryMaintenancePlanItems.length > 0
                    ? this.selectedOperations.concat(this.mandatoryMaintenancePlanItems.map(item => item.operationId))
                    : this.selectedOperations;

                // Transform selected operations in operations with recurrences
                this.operationsWithRecurrences = MaintenancePlanUtils.groupMaintenancePlanItems(this.maintenancePlanItems,
                    this.mandatoryMaintenancePlanItems,
                    allOperationIds,
                    this.translate.instant('admin.toolMaintenancePlans.recurrences.recurringEvery'));

                // Initialed the operationsWithRecurrencesForm with preselected recurrences
                this.operationsWithRecurrencesForm.setValue({
                    'selectedRecurrencesItems': MaintenancePlanUtils.groupSelectedOperations(this.operationsWithRecurrences)
                });

                this.isLoading = false;

            });

            this.toolUsageService.getProductionData(toolIds).subscribe((data: RevisionInfo[]) => {
                this.revisionInfo = data;

                this.isLoading = false;
            });
        }
        if (step === 2) {
            const groupedTools = MaintenancePlanUtils.groupTools(this.issue.tools);
            const operationsWithSelectedRecurrences = this.operationsWithRecurrencesForm.controls.selectedRecurrencesItems.value;

            this.ticketOperations = MaintenancePlanUtils.computeTicketOperations(groupedTools,
                operationsWithSelectedRecurrences,
                this.maintenancePlanItems)

            this.overviewItems = MaintenancePlanUtils.buildOverviewItems(groupedTools,
                operationsWithSelectedRecurrences,
                this.issue.tools,
                this.maintenancePlanItems,
                this.translate.instant(
                    'admin.toolMaintenancePlans.recurrences.recurringEvery'
                ));
        }
    }

    canContinueStep1() {
        return this.ticketForm.valid
            || (this.mandatoryMaintenancePlanItems.length > 0 && this.ticketForm.get('description').valid);
    }

    onStepErrosChange() {
        this.showValidations = !this.ticketForm.valid;
    }

    resetForm() {
        this._logger.info('resetForm:: Resetting form');
        this.showValidations = false;
        this.modal.reset();
        this.selectedRecurrencesItems = []
        this.issue = new Issue();
    }

    toolsHaveMaintenancePlan() {
        return this.issue && this.issue.tools[0] && this.issue.tools[0].maintenancePlanId !== null;
    }

    openRevisionModal() {
        this.revisionInfoModal.open(this.revisionInfo);
    }
}
